import {
	CustomerProductControllerApiFactory,
	type DraftEmissionRequestFormData,
	type EmailToken,
	FormTemplateControllerApiFactory,
	type FormTemplateResponse,
	OrganisationInvitationControllerApiFactory,
	type SupplierProductIdPair,
	type TemplateCreateRequest,
} from '@api-client/api'
import { AsyncButton } from '@components/AsyncButton'
import { CTAPcfAtScale } from '@components/CTAPcfAtScale'
import { Loading } from '@components/Loading'
import type { ProductType } from '@components/ProductForm'
import { isValidEmail } from '@helpers/strings'
import { useAsyncApi } from '@hooks/useAsyncApi'
import useSuppliersList from '@hooks/useSuppliersList'
import { URLFeatureFlag, useUrlFeatureFlag } from '@hooks/useUrlFeatureFlag'
import { Box, Button, Modal, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ProductForm } from '../../ProductForm'
import { UploadProducts, useBulkImportChangeFileCallback } from '../UploadModal'
import {
	AddProductModeSelector,
	ProductImportMode,
} from './AddProductModeSelector'
import * as S from './styles'

type AddProductStep = 'selectMode' | 'addProduct'

type ProductModalContext = 'product' | 'template'

enum SupplierSelectedState {
	ERROR,
	EXISTING,
	NEW,
	NONE,
}

type TemplateType = Omit<TemplateCreateRequest, 'supplierLinks'> & {
	supplierId?: string
	emissionData?: DraftEmissionRequestFormData
}

interface IProps {
	isModalOpened: boolean
	isUpdating?: boolean
	context: ProductModalContext
	onClose: () => void
	onProductsAdded?: (productIds: readonly SupplierProductIdPair[]) => void
	saveNewProduct: (data: ProductType) => Promise<void>
	initialValues?: ProductType
	initialState?: { step: AddProductStep; mode: ProductImportMode }
	isSaving?: boolean
	onProductBulkImport?: () => void
	displaySupplierSelection?: boolean
}

// TODO: refactor this component to rely on reuse between this and TemplateImportModal
const ProductModal = (props: IProps) => {
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const {
		context,
		isModalOpened,
		isUpdating = false,
		onClose,
		saveNewProduct,
		initialValues,
		initialState,
		isSaving,
		onProductBulkImport,
		onProductsAdded,
		displaySupplierSelection,
	} = props

	const { data: supplierData, refetch: refetchSuppliers } = useSuppliersList()

	const mappedSupplierData = useMemo(
		() => supplierData?.data.map(({ id, name }) => ({ id, name })) || [],
		[supplierData?.data]
	)

	const [mode, setMode] = useState<ProductImportMode>(ProductImportMode.MANUAL)
	const [isLoading, setIsLoading] = useState(false)
	const [step, setStep] = useState<AddProductStep>('selectMode')
	const productControllerApi = useAsyncApi(CustomerProductControllerApiFactory)

	const isFormTemplateEnabled = useUrlFeatureFlag(URLFeatureFlag.FormTemplate)
	const formTemplateApi = useAsyncApi(FormTemplateControllerApiFactory)
	const [formTemplates, setFormTemplates] = useState<
		readonly FormTemplateResponse[]
	>([])

	useEffect(() => {
		if (isFormTemplateEnabled) {
			formTemplateApi(api => api.listTemplates1()).then(response => {
				setFormTemplates(
					(response as { data: readonly FormTemplateResponse[] }).data
				)
				return undefined
			})
		}
	}, [isFormTemplateEnabled, formTemplateApi])

	const importProducts = useCallback(
		(file: File, allowWarnings?: boolean, dryRun?: boolean) =>
			productControllerApi(async api => {
				const resp = await api.importProducts(file, allowWarnings, dryRun)
				return resp.data
			}),
		[productControllerApi]
	)
	const [selectedFile, isFileValid, violations, handleFileChange] =
		useBulkImportChangeFileCallback(
			file => importProducts(file, true, true),
			[importProducts]
		)
	const importReady = !!selectedFile && isFileValid

	const form = useForm<ProductType>({
		defaultValues: {
			supplierId: '',
			name: '',
			description: '',
			internalReference: '',
			unit: undefined,
			emissionData: undefined,
			formTemplateId: undefined,
		},
		mode: 'onSubmit',
	})

	const isSupplierAlreadySelected = useCallback(
		() => initialState?.step === 'addProduct',
		[initialState]
	)

	const resetModal = useCallback(() => {
		form.reset()
		setMode(ProductImportMode.MANUAL)
		setStep('selectMode')
	}, [form])

	const invitationControllerApi = useAsyncApi(
		OrganisationInvitationControllerApiFactory
	)

	useEffect(() => {
		if (initialState) {
			setStep(initialState?.step)
			setMode(initialState?.mode)
		}
	}, [initialState])

	useEffect(() => {
		if (initialValues) {
			form.setValue('supplierId', initialValues?.supplierId)
			form.setValue('name', initialValues?.name)
			form.setValue('description', initialValues?.description)
			form.setValue('internalReference', initialValues?.internalReference)
			form.setValue('unit', initialValues?.unit)
			form.setValue('emissionData', initialValues?.emissionData)
			if (context === 'product') {
				form.setValue('purchasingPeriodEnd', initialValues?.purchasingPeriodEnd)
				form.setValue(
					'purchasingPeriodStart',
					initialValues?.purchasingPeriodStart
				)
			}
		}
	}, [form, isModalOpened, initialValues, context])

	const getSupplierInputState =
		useCallback(async (): Promise<SupplierSelectedState> => {
			if (isSupplierAlreadySelected()) {
				return SupplierSelectedState.NONE
			}
			if (
				!(await form.trigger(
					!isSupplierAlreadySelected() ? ['supplierId'] : []
				))
			) {
				return SupplierSelectedState.ERROR
			}
			const supplierIdentifier = form.getValues().supplierId
			if (supplierData.data.some(d => d.id === supplierIdentifier)) {
				return SupplierSelectedState.EXISTING
			}
			if (supplierIdentifier && isValidEmail(supplierIdentifier)) {
				return SupplierSelectedState.NEW
			}
			return SupplierSelectedState.ERROR
		}, [form, isSupplierAlreadySelected, supplierData.data])

	/**
	 * Verify if the supplier provided is correct:
	 * 1. A supplier is required if supplierSelection is true
	 * 2. Supplier selected match an existing supplier
	 * 3. Supplier selected is an email -> it will create a new "inflight" supplier
	 **/
	const validateSupplier = useCallback(async (): Promise<boolean> => {
		form.setValue('supplierId', form.getValues().supplierId?.trim())
		const supplierInputState = await getSupplierInputState()
		if (supplierInputState === SupplierSelectedState.ERROR) {
			form.setError('supplierId', {
				type: 'validate',
				message: t('formField.autocompleteSupplierError'),
			})
			return false
		}
		return true
	}, [form, getSupplierInputState, t])

	const closeModal = useCallback(() => {
		resetModal()
		onClose()
	}, [onClose, resetModal])

	const saveSupplier = useCallback(
		async (supplierEmail: string): Promise<EmailToken> => {
			setIsLoading(true)
			try {
				const response = await invitationControllerApi(api =>
					api.createInvitation({
						emailsToNotifyPerPrimaryEmail: { [supplierEmail]: [] },
					})
				)
				return response.data.emailTokens[0]
			} catch (error) {
				if (error instanceof Error && 'response' in error) {
					const axiosError = error as {
						response?: { status: number; data: unknown }
					}
					if (axiosError.response?.status === 409) {
						const responseData = axiosError.response.data
						if (
							Array.isArray(responseData) &&
							responseData.length > 0 &&
							responseData[0].status === 'CONFLICTING'
						) {
							const email = responseData[0].email
							const domain = email?.split('@')[1] || ''
							throw new Error(
								t('toast.error.productConflictSameOrganisation', { domain }),
								{ cause: error }
							)
						}
					}
				}
				throw error
			} finally {
				setIsLoading(false)
			}
		},
		[invitationControllerApi, t]
	)

	const saveBulkProducts = useCallback(async () => {
		if (!selectedFile || !isFileValid) {
			return
		}
		try {
			const success = await importProducts(selectedFile, true, false)
			if (!success) {
				return
			}
			onProductsAdded?.(success.addedProducts)
			onProductBulkImport?.()
			closeModal()
		} catch (error) {
			if (error instanceof Error && 'response' in error) {
				const axiosError = error as {
					response?: { status: number; data: unknown }
				}
				if (axiosError.response?.status === 409) {
					const responseData = axiosError.response.data
					if (
						Array.isArray(responseData) &&
						responseData.length > 0 &&
						responseData[0].status === 'CONFLICTING'
					) {
						const email = responseData[0].email
						const domain = email?.split('@')[1] || ''
						enqueueSnackbar(
							t('toast.error.productConflictSameOrganisation', { domain }),
							{ variant: 'error' }
						)
						throw error
					}
				}
			}
			throw error
		}
	}, [
		closeModal,
		onProductBulkImport,
		onProductsAdded,
		importProducts,
		selectedFile,
		isFileValid,
		enqueueSnackbar,
		t,
	])

	const saveManualProduct = useCallback(async (): Promise<boolean> => {
		if (
			await form?.trigger(
				!isSupplierAlreadySelected
					? ['supplierId', 'name', 'description', 'unit', 'internalReference']
					: ['name', 'description', 'unit', 'internalReference']
			)
		) {
			let supplierId = form.getValues().supplierId
			setIsLoading(true)
			try {
				if (
					supplierId &&
					(await getSupplierInputState()) === SupplierSelectedState.NEW
				) {
					try {
						supplierId = (
							await saveSupplier(form.getValues().supplierId as string)
						).id
					} catch (error) {
						if (error instanceof Error) {
							enqueueSnackbar(error.message, { variant: 'error' })
						}
						throw error
					}
				}
				await saveNewProduct({ ...form.getValues(), supplierId })
				return true
			} finally {
				setIsLoading(false)
			}
		}
		return false
	}, [
		form,
		isSupplierAlreadySelected,
		getSupplierInputState,
		saveNewProduct,
		saveSupplier,
		enqueueSnackbar,
	])

	const [isSavingInternally, setIsSaving] = useState(false)
	const nextOrSaveProduct = useCallback(async () => {
		if (step === 'selectMode') {
			// First step: just move to the next step without validation
			setStep('addProduct')
			return
		}

		// Second step: validate supplier only in manual mode and if supplier selection is enabled.
		if (mode === ProductImportMode.MANUAL && displaySupplierSelection) {
			if (await validateSupplier()) {
				// save supplier if supplier is NEW and BULK is selected to allow CSV import dry run
				if ((await getSupplierInputState()) === SupplierSelectedState.NEW) {
					try {
						const supplier = await saveSupplier(
							form.getValues().supplierId as string
						)
						form.setValue('supplierId', supplier.id)
						refetchSuppliers()
					} catch (error) {
						if (error instanceof Error) {
							enqueueSnackbar(error.message, { variant: 'error' })
						}
						return
					}
				}
			} else {
				return
			}
		}

		setIsSaving(true)
		let saved = false
		try {
			if (mode === ProductImportMode.MANUAL) {
				saved = await saveManualProduct()
			} else {
				await saveBulkProducts()
				saved = true
			}
		} catch {
			return
		} finally {
			setIsSaving(false)
		}
		if (saved) {
			resetModal()
		}
	}, [
		step,
		mode,
		displaySupplierSelection,
		resetModal,
		validateSupplier,
		getSupplierInputState,
		saveSupplier,
		form,
		refetchSuppliers,
		saveManualProduct,
		saveBulkProducts,
		enqueueSnackbar,
	])

	const cancelOrPrevious = useCallback(() => {
		if (step === 'addProduct' && !isSupplierAlreadySelected()) {
			setStep('selectMode')
			return
		}
		closeModal()
	}, [step, isSupplierAlreadySelected, closeModal])

	const isSaveable = useCallback(() => {
		if (step === 'addProduct' && mode === ProductImportMode.BULK) {
			return importReady
		}
		return true
	}, [step, mode, importReady])

	const isPending = isSavingInternally || isSaving || isLoading

	const translationContext = (isUpdating ? 'update_' : '') + context
	const modalHeader = (
		<S.Header>
			<Typography variant="h2">
				{t('modal.addProduct.title', {
					context: translationContext,
				})}
			</Typography>
		</S.Header>
	)

	return (
		<Modal open={isModalOpened} onClose={closeModal}>
			<S.BackdropWithModalStyles>
				{modalHeader}
				<S.ContentContainer $isOpaque={isPending}>
					<FormProvider {...form}>
						{step === 'selectMode' && (
							<AddProductModeSelector mode={mode} setMode={setMode} />
						)}
						{step === 'addProduct' && mode === ProductImportMode.MANUAL && (
							<ProductForm
								hasRequestCommentField
								displaySupplierSelection={displaySupplierSelection}
								control={form.control}
								supplierData={mappedSupplierData}
								disabled={!!initialValues?.supplierId}
								clearErrors={form.clearErrors}
								getValues={form.getValues}
								hidePurchasingPeriod={context === 'template'}
								formTemplates={
									isFormTemplateEnabled ? formTemplates : undefined
								}
							/>
						)}
						{step === 'addProduct' && mode === ProductImportMode.BULK && (
							<UploadProducts
								selectedFile={selectedFile}
								isFileValid={isFileValid}
								violations={violations}
								onChangeFile={handleFileChange}
								isLoading={isPending}
							/>
						)}
						{isPending && (
							<S.LoadingContainer>
								<Loading />
							</S.LoadingContainer>
						)}
					</FormProvider>
					{isPending && (
						<S.LoadingContainer>
							<Loading />
						</S.LoadingContainer>
					)}
				</S.ContentContainer>
				<S.Footer>
					<Box>
						<CTAPcfAtScale />
					</Box>
					<Stack spacing={2} direction="row">
						<Button
							variant="outlined"
							color="primary"
							data-testid="cancel"
							onClick={cancelOrPrevious}
							disabled={isPending}
						>
							{step === 'addProduct' && !isSupplierAlreadySelected()
								? t('button.previous')
								: t('button.cancel')}
						</Button>
						<AsyncButton
							variant="contained"
							color="primary"
							data-testid="submit"
							onClick={nextOrSaveProduct}
							disabled={!isSaveable() || isPending}
						>
							{step === 'selectMode'
								? t('button.next')
								: isUpdating
									? t('button.updateTemplate')
									: t('button.save')}
						</AsyncButton>
					</Stack>
				</S.Footer>
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { ProductModal, type ProductModalContext, type TemplateType }
